import React, { useCallback, useEffect, useRef, useState } from 'react';
import { annotateMergedDocument, annotateTemplate, DOCX_CLASS, IPreviewToken, PreviewTokenKind, tokenValues } from './docxPreviewDom';
import { ensureStyles, PREVIEW_PANE_CSS } from './docxPreviewStyles';
import { renderDocx } from './renderDocx';

export interface IDocxPreviewPaneProps {
    /** The .docx to show; the pane re-renders whenever this changes. */
    data: ArrayBuffer;
    /** merged: a preview-mode merge from the server. template: a raw template whose {Tokens} are classified. */
    mode: 'merged' | 'template';
    isKnownToken?: (token: string) => boolean;
    describeToken?: (token: string, kind: PreviewTokenKind) => string;
    onTokenClick?: (token: string, kind: PreviewTokenKind) => void;
    onRendered?: (tokens: IPreviewToken[]) => void;
    onRenderError?: (message: string) => void;
    /** Off shows the document as it will print, without highlighting. */
    highlight?: boolean;
    /** Scroll to whatever just changed when none of it is in view. */
    followChanges?: boolean;
    /** CSS zoom factor, or 'fit' to fit the page width. */
    zoom?: number | 'fit';
    style?: React.CSSProperties;
}

/**
 * Renders a Word document in the browser. Each new document is rendered off-screen and swapped in when
 * ready, so the page never flashes blank and the scroll position survives a refresh.
 */
const DocxPreviewPane = (props: IDocxPreviewPaneProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const pageHostRef = useRef<HTMLDivElement>(null);
    const stagingRef = useRef<HTMLDivElement>(null);
    const renderCount = useRef(0);
    const previousValues = useRef<Map<string, string>>(null);
    const pageWidth = useRef(0);
    const [fitZoom, setFitZoom] = useState(1);

    // The render is async; read callbacks through a ref so a new closure doesn't restart it.
    const latest = useRef(props);
    latest.current = props;

    const updateFitZoom = useCallback(() => {
        const scroller = scrollRef.current;
        if (scroller && pageWidth.current > 0) {
            setFitZoom(Math.min(1.5, Math.max(0.3, (scroller.clientWidth - 40) / pageWidth.current)));
        }
    }, []);

    useEffect(() => {
        const doc = scrollRef.current.ownerDocument;
        ensureStyles(doc, 'lh-docx-preview-styles', PREVIEW_PANE_CSS);

        const view = doc.defaultView;
        view.addEventListener('resize', updateFitZoom);
        return () => view.removeEventListener('resize', updateFitZoom);
    }, [updateFitZoom]);

    useEffect(() => {
        if (!props.data) {
            return;
        }

        const renderId = ++renderCount.current;
        const doc = stagingRef.current.ownerDocument;
        const styles = doc.createElement('div');
        const body = doc.createElement('div');
        stagingRef.current.appendChild(styles);
        stagingRef.current.appendChild(body);
        const discard = () => {
            styles.remove();
            body.remove();
        };

        renderDocx(props.data, body, styles)
            .then(() => {
                if (renderId !== renderCount.current) {
                    discard();
                    return;
                }

                const current = latest.current;
                const tokens =
                    current.mode === 'template' ? annotateTemplate(body, current.isKnownToken || (() => false)) : annotateMergedDocument(body);
                if (current.describeToken) {
                    tokens.forEach((t) => (t.element.title = current.describeToken(t.token, t.kind)));
                }

                const values = tokenValues(tokens);
                const changed = previousValues.current
                    ? tokens.filter((t) => previousValues.current.get(t.token) !== values.get(t.token)).map((t) => t.element)
                    : [];
                changed.forEach((element) => element.classList.add('lh-token--changed'));
                previousValues.current = values;

                const firstPage = body.querySelector(`section.${DOCX_CLASS}`) as HTMLElement;
                pageWidth.current = firstPage ? firstPage.offsetWidth : 0;

                const scroller = scrollRef.current;
                const host = pageHostRef.current;
                const scrollTop = scroller.scrollTop;
                while (host.firstChild) {
                    host.removeChild(host.firstChild);
                }
                host.appendChild(styles);
                host.appendChild(body);
                scroller.scrollTop = scrollTop;
                updateFitZoom();

                if (current.followChanges && changed.length > 0) {
                    const view = scroller.getBoundingClientRect();
                    const inView = changed.some((element) => {
                        const box = element.getBoundingClientRect();
                        return box.bottom > view.top && box.top < view.bottom;
                    });
                    if (!inView) {
                        changed[0].scrollIntoView({ block: 'center' });
                    }
                }

                if (current.onRendered) {
                    current.onRendered(tokens);
                }
            })
            .catch((error: Error) => {
                discard();
                if (renderId === renderCount.current && latest.current.onRenderError) {
                    latest.current.onRenderError((error && error.message) || 'The document could not be displayed.');
                }
            });
    }, [props.data, props.mode, props.isKnownToken, updateFitZoom]);

    const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!props.onTokenClick) {
            return;
        }
        const element = (event.target as HTMLElement).closest('[data-lh-token]');
        if (element) {
            props.onTokenClick(element.getAttribute('data-lh-token'), element.getAttribute('data-lh-kind') as PreviewTokenKind);
        }
    };

    const classes = ['lh-preview-scroll'];
    if (props.highlight === false) {
        classes.push('lh-preview--plain');
    }
    if (props.onTokenClick) {
        classes.push('lh-preview--clickable');
    }

    return (
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, ...props.style }}>
            <div ref={scrollRef} className={classes.join(' ')} style={{ flex: 1, minHeight: 0 }} onClick={onClick}>
                <div ref={pageHostRef} style={{ zoom: props.zoom === 'fit' || props.zoom === undefined ? fitZoom : props.zoom }} />
            </div>
            <div ref={stagingRef} className="lh-preview-staging" aria-hidden={true} />
        </div>
    );
};

export default DocxPreviewPane;



// WEBPACK FOOTER //
// ./src/components/common/docxPreview/DocxPreviewPane.tsx